import {
  MAILBOX_PRICING,
  PRICING_META,
  PRICING_MODULES,
  bundleQuote,
  isCheckoutModuleId,
} from "./content";

export type MailBilling = "monthly" | "annual";

export type CheckoutCart = {
  moduleIds: string[];
  mailboxQty: number;
  mailBilling: MailBilling;
  orgName?: string;
  contactEmail?: string;
};

const DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/i;

export function parseCart(raw: unknown): CheckoutCart | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const moduleIds = Array.isArray(o.moduleIds)
    ? o.moduleIds.map(String).filter(isCheckoutModuleId)
    : [];
  const mailboxQty = Math.min(500, Math.max(0, Math.floor(Number(o.mailboxQty) || 0)));
  const mailBilling: MailBilling =
    o.mailBilling === "annual" ? "annual" : "monthly";
  if (moduleIds.length === 0 && mailboxQty === 0) return null;
  return {
    moduleIds: [...new Set(moduleIds)],
    mailboxQty,
    mailBilling,
    orgName: o.orgName != null ? String(o.orgName).trim().slice(0, 120) : undefined,
    contactEmail:
      o.contactEmail != null ? String(o.contactEmail).trim().toLowerCase() : undefined,
  };
}

export function validateDomain(domain: string): boolean {
  const d = domain.trim().toLowerCase();
  return d.length >= 4 && d.length <= 253 && DOMAIN_RE.test(d);
}

export function cartTotals(cart: CheckoutCart) {
  const modules = bundleQuote(cart.moduleIds);
  const { monthlyPerMailbox, annualPerMailbox } = MAILBOX_PRICING;
  const mailSubtotal =
    cart.mailboxQty > 0
      ? cart.mailBilling === "annual"
        ? cart.mailboxQty * annualPerMailbox
        : cart.mailboxQty * monthlyPerMailbox
      : 0;
  const subtotal = modules.subtotal + mailSubtotal;
  const discount =
    modules.count >= 2
      ? Math.round((subtotal * PRICING_META.bundleDiscountPercent) / 100)
      : 0;
  return {
    modules,
    mailSubtotal,
    subtotal,
    discount,
    total: subtotal - discount,
    moduleCount: modules.count,
  };
}

/** Map checkout module IDs to JD Account app IDs for provisioning. */
export function moduleIdsToAppIds(moduleIds: readonly string[]): string[] {
  const map: Record<string, string> = {
    "suite-calendar": "calendar",
    "suite-task": "task",
    "suite-analytics": "analytics",
    "suite-crm": "crm",
    "suite-cms": "cms",
  };
  const apps = moduleIds.map((id) => map[id]).filter(Boolean);
  if (apps.length) apps.unshift("account");
  return [...new Set(apps)];
}

export function appIdsForOrder(
  moduleIds: readonly string[],
  mailboxQty: number,
): string[] {
  const apps = moduleIdsToAppIds(moduleIds);
  if (apps.length > 0 && !apps.includes("drive")) apps.push("drive");
  if (mailboxQty > 0 && !apps.includes("mail")) apps.push("mail");
  return apps;
}

export function cartLineItems(cart: CheckoutCart) {
  const items: { label: string; amount: number; unit?: string }[] = [];
  for (const id of cart.moduleIds) {
    const mod = PRICING_MODULES.find((m) => m.id === id);
    if (mod) {
      items.push({
        label: mod.name,
        amount: mod.monthlyList,
        unit: "/ org / month",
      });
    }
  }
  if (cart.mailboxQty > 0) {
    const { monthlyPerMailbox, annualPerMailbox } = MAILBOX_PRICING;
    items.push({
      label: `JD Mail mailboxes × ${cart.mailboxQty}`,
      amount:
        cart.mailBilling === "annual"
          ? cart.mailboxQty * annualPerMailbox
          : cart.mailboxQty * monthlyPerMailbox,
      unit:
        cart.mailBilling === "annual" ? "/ mailbox / year" : "/ mailbox / month",
    });
  }
  return items;
}

export const CART_STORAGE_KEY = "jdi-checkout-cart";
